import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Bell, Clock, Check, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useAuth } from '../context/AuthContext';
import { DiscussionsFicheTab } from './DiscussionsFicheTab';
import ReminderModal from './ReminderModal';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDatetime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

const EVT_LABELS = {
  creation:           'Créé',
  modification:       'Modifié',
  statut_change:      'Statut modifié',
  envoi_email:        'Email envoyé',
  email_envoye:       'Email envoyé',
  revision_creee:     'Révision créée',
  revision_restauree: 'Version restaurée',
  reception_creee:    'Réception créée',
  reception_validee:  'Réception validée',
  document_ajoute:    'Document ajouté',
  commentaire:        'Commentaire',
};

function evtLabel(ev) {
  return EVT_LABELS[ev.event_type]
    || (ev.event_type || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
    || 'Événement';
}

// ── Entête de colonne ─────────────────────────────────────────────────────────

function ColHeader({ icon, label, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '7px 12px', borderBottom: '1px solid var(--border-light)',
      background: '#FAFAF8', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      </div>
      {action}
    </div>
  );
}

// ── Colonne Rappels ───────────────────────────────────────────────────────────

function RappelsColumn({ type_document, record_id, numero_document }) {
  const [rappels,  setRappels]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();

  const load = useCallback(async () => {
    if (!record_id) return;
    setLoading(true);
    try {
      const r = await api.getReminders({ document_id: record_id });
      setRappels(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [record_id]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (rem) => {
    const newStatut = rem.statut === 'actif' ? 'fait' : 'actif';
    try {
      await api.patchReminder(rem.reminder_id, { statut: newStatut });
      setRappels(prev => prev.map(r =>
        r.reminder_id === rem.reminder_id ? { ...r, statut: newStatut } : r
      ));
    } catch { toast.error('Erreur mise à jour'); }
  };

  const handleDelete = async (rem) => {
    try {
      await api.deleteReminder(rem.reminder_id);
      setRappels(prev => prev.filter(r => r.reminder_id !== rem.reminder_id));
      toast.success('Rappel supprimé');
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <>
      <ColHeader
        icon={<Bell size={11} style={{ color: '#8B5CF6' }} />}
        label="Rappels"
        action={
          <button onClick={() => setCreating(true)} data-testid="add-rappel-btn"
            style={{ width: 18, height: 18, borderRadius: 4, border: '1px solid #DDD6FE', background: '#F5F3FF', color: '#7C3AED', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
            +
          </button>
        }
      />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Loader2 size={14} className="animate-spin" style={{ color: '#8B5CF6' }} />
          </div>
        ) : rappels.length === 0 ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
            Aucun rappel
          </div>
        ) : (
          rappels.map(rem => {
            const isFait = rem.statut === 'fait';
            const over   = !isFait && isOverdue(rem.date_echeance);
            return (
              <div key={rem.reminder_id}
                style={{ padding: '7px 10px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                {/* Bouton toggle statut — visible uniquement par l'assigné (ou créateur si non assigné) */}
                {(!rem.assigned_to_id || rem.assigned_to_id === user?.user_id) && (
                  <button onClick={() => handleToggle(rem)} data-testid={`toggle-rem-${rem.reminder_id}`}
                    title={isFait ? 'Marquer actif' : 'Marquer fait'}
                    style={{ width: 15, height: 15, borderRadius: '50%', border: `2px solid ${isFait ? '#10B981' : '#8B5CF6'}`, background: isFait ? '#10B981' : 'white', flexShrink: 0, marginTop: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isFait && <Check size={8} style={{ color: 'white', strokeWidth: 3 }} />}
                  </button>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 10, color: over ? '#DC2626' : 'var(--text-muted)', fontWeight: over ? 600 : 400 }}>
                      {fmtDate(rem.date_echeance)}
                    </span>
                    {rem.assigned_to_nom && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>· {rem.assigned_to_nom}</span>
                    )}
                  </div>
                  <p style={{ fontSize: 11, color: isFait ? 'var(--text-muted)' : 'var(--text)', textDecoration: isFait ? 'line-through' : 'none', lineHeight: 1.4, margin: 0 }}>
                    {rem.texte}
                  </p>
                </div>
                {rem.created_by_id === user?.user_id && (
                  <button onClick={() => handleDelete(rem)} title="Supprimer"
                    data-testid={`delete-rem-${rem.reminder_id}`}
                    style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Trash2 size={11} style={{ color: '#DC2626' }} />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      <ReminderModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(r) => { setRappels(prev => [r.data || r, ...prev]); setCreating(false); }}
        typeDocument={type_document}
        documentId={record_id}
        numeroDocument={numero_document}
      />
    </>
  );
}

// ── Colonne Activités ─────────────────────────────────────────────────────────

function ActivitesColumn({ activiteFetcher, recordId }) {
  const [activites, setActivites] = useState([]);
  const [loading,   setLoading]   = useState(false);

  const load = useCallback(async () => {
    if (!activiteFetcher || !recordId) return;
    setLoading(true);
    try {
      const data = await activiteFetcher();
      setActivites(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [activiteFetcher, recordId]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <ColHeader
        icon={<Clock size={11} style={{ color: '#9CA3AF' }} />}
        label="Activités"
      />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!activiteFetcher ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.6 }}>
            Journal d'activité<br />à venir
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Loader2 size={14} className="animate-spin" style={{ color: '#D1D5DB' }} />
          </div>
        ) : activites.length === 0 ? (
          <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
            Aucune activité
          </div>
        ) : (
          activites.map((ev, i) => (
            <div key={ev.event_id || i}
              style={{ display: 'flex', gap: 7, padding: '6px 10px', borderBottom: '1px solid var(--border-light)' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#D1D5DB', flexShrink: 0, marginTop: 6 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 11, color: 'var(--text)', fontWeight: 500, lineHeight: 1.3, marginBottom: 1 }}>
                  {evtLabel(ev)}
                </p>
                <p style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3 }}>
                  {ev.created_by_nom || ev.auteur_nom || '—'} · {fmtDatetime(ev.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ── Hook — détection d'activités ──────────────────────────────────────────────

export function useHasActivite(record_type, record_id) {
  const [hasActivity, setHasActivity] = useState(false);

  useEffect(() => {
    if (!record_id) { setHasActivity(false); return; }
    setHasActivity(false);
    let cancelled = false;
    Promise.all([
      api.getReminders({ document_id: record_id }).catch(() => ({ data: [] })),
      api.getDiscussionByRecord(record_type, record_id).catch(() => ({ data: [] })),
    ]).then(([remRes, discRes]) => {
      if (cancelled) return;
      const activeRems = (Array.isArray(remRes.data) ? remRes.data : []).filter(r => r.statut === 'actif');
      // discRes.data est un objet thread (ou null) — pas un tableau
      const hasDisc = discRes.data != null && ((discRes.data.messages || []).length > 0 || (discRes.data.participants || []).length > 0);
      setHasActivity(activeRems.length > 0 || hasDisc);
    });
    return () => { cancelled = true; };
  }, [record_type, record_id]);

  return { hasActivity };
}

// ── Indicateur d'activité animé (cloche qui sonne) ────────────────────────────

export function ActivityPulse() {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 3 }}>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Bell
          size={13}
          style={{ color: '#F59E0B' }}
        />
        <span style={{
          position: 'absolute', top: -2, right: -2,
          width: 5, height: 5,
          borderRadius: '50%',
          background: '#EF4444',
          border: '1.5px solid white',
        }} />
      </span>
    </span>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

/**
 * ActivitesFichePanel — panneau 3 colonnes Discussions | Rappels | Activités
 *
 * Props:
 *   record_type     : 'achat'|'vente'|'location'|'atelier'|'entreprise'
 *   record_id       : string
 *   record_label    : string
 *   type_document   : string (pour l'API reminders)
 *   numero_document : string
 *   activiteFetcher : () => Promise<Array> | null  (null = placeholder)
 *   created_by_nom  : string | null  (affiche "Affaire suivie par")
 */
export function ActivitesFichePanel({
  record_type,
  record_id,
  record_label,
  type_document,
  numero_document,
  activiteFetcher = null,
  created_by_nom,
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid var(--border-light)',
      borderRadius: 10,
      overflow: 'hidden',
      background: 'white',
    }}>

      {/* Bandeau "Affaire suivie par" — toujours visible si prop fournie */}
      {created_by_nom !== undefined && (
        <div style={{
          padding: '5px 14px',
          borderBottom: '1px solid var(--border-light)',
          background: '#FAFAF8',
          fontSize: 11,
          color: 'var(--text-secondary)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}>
          <span style={{ color: 'var(--text-muted)' }}>Affaire suivie par :</span>
          <span style={{ fontWeight: 600, color: created_by_nom ? 'var(--text)' : 'var(--text-muted)' }}>
            {created_by_nom || '-'}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', height: 500 }}>

      {/* Colonne 1 — Discussions (45 %) */}
      <div style={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-light)', minWidth: 0, overflow: 'hidden' }}>
        <ColHeader
          icon={<MessageCircle size={11} style={{ color: '#3B82F6' }} />}
          label="Discussions"
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <DiscussionsFicheTab
            record_type={record_type}
            record_id={record_id}
            record_label={record_label}
          />
        </div>
      </div>

      {/* Colonne 2 — Rappels (28 %) */}
      <div style={{ flex: '0 0 28%', display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-light)', minWidth: 0, overflow: 'hidden' }}>
        <RappelsColumn
          type_document={type_document}
          record_id={record_id}
          numero_document={numero_document}
        />
      </div>

      {/* Colonne 3 — Activités (27 %) */}
      <div style={{ flex: '0 0 27%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        <ActivitesColumn
          activiteFetcher={activiteFetcher}
          recordId={record_id}
        />
      </div>

      </div>
    </div>
  );
}
